import React, { useEffect, useRef, useState } from 'react';
import { BillsService } from 'services/bills/bills';
import { finalize } from 'rxjs/operators';
import { SingleUser } from 'utils/api/models/users/singleUser';
import { UsersService } from 'services/users/users';
import { Route, Switch, useHistory } from 'react-router';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { AccountsService } from 'services/accounts/accounts';
import { useDispatch } from 'react-redux';
import { IAlternativeSelectItem } from 'components/AlternativeSelect/AlternativeSelect';
import { SingleBillId } from 'utils/api/models/bills/singleBillId';
import { BreadcrumbsItem } from 'react-breadcrumbs-dynamic';
import MobileService from './MobileService';
import FixedBusinessBroadband from './FixedBusinessBroadband';
import { serviceType } from './constants';
import { translate } from 'components/Translation';
import PSTN from './PSTN';
import AddNewMb from 'views/Microbundle/AddNewMb';
import { ROUTES } from 'views/Microbundle/AddNewMb/constants';

interface IAddNewServicesProps {
	match: {
		params: {
			accountId: string;
			type: 'postpaid' | 'data';
		};
		path: string;
	};
	showConfirmationDialog: (selectedNumber: string, simDetails?: types.newServices.ISimDetails) => void;
	mainContainerRef?: React.RefObject<HTMLDivElement>;
}

const AddSingleService = ({ match, showConfirmationDialog, mainContainerRef }: IAddNewServicesProps) => {
	const [billingAccounts, setBillingAccounts] = useState<SingleBillId[]>([]);
	const [loadingBillingAccounts, setLoadingBillingAccounts] = useState(true);
	const [users, setUsers] = useState<SingleUser[]>([]);
	const [filteredUsers, setFilteredUsers] = useState<SingleUser[]>([]);
	const [loadingUsers, setLoadingUsers] = useState(true);
	const [selectedAccount, setSelectedAccount] = useState<IAlternativeSelectItem | null>(null);
	const [loadingAccounts, setLoadingAccounts] = useState(true);
	const { accountId, type } = match.params;
	const dispatch = useDispatch();
	const loading: boolean = loadingUsers || loadingBillingAccounts || loadingAccounts;
	const history = useHistory();
	const isMounted = useRef(true);

	useEffect(() => {
		BillsService.getCorpBillingIds()
			.pipe(finalize(() => isMounted.current && setLoadingBillingAccounts(false)))
			.subscribe(({ billingIds }) => isMounted.current && setBillingAccounts(billingIds));
	}, []);

	useEffect(() => {
		AccountsService.getCorpAccountsForCurrentUser()
			.pipe(finalize(() => isMounted.current && setLoadingAccounts(false)))
			.subscribe(({ accounts }) => {
				if (isMounted.current) {
					const account = accounts.find((singleAccount) => singleAccount.accountId === accountId);
					setSelectedAccount(
						account
							? {
									id: account.accountId,
									name: account.accountName,
							  }
							: null
					);
				}
			});
	}, [accountId]);

	useEffect(() => {
		UsersService.getCorpUsersForAccounts()
			.pipe(finalize(() => isMounted.current && setLoadingUsers(false)))
			.subscribe(({ users }) => isMounted.current && setUsers(users));
	}, []);

	useEffect(() => {
		setFilteredUsers(users.filter((user) => user.accountId === accountId));
	}, [users, accountId]);

	useEffect(() => {
		if (!loadingBillingAccounts && !loadingUsers && !loadingAccounts) {
			dispatch(hideLoadingIndicator());
		} else {
			dispatch(showLoadingIndicator());
		}
	}, [loadingBillingAccounts, loadingUsers, loadingAccounts, dispatch]);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	const back = () => {
		history.push(`/dashboard/services`);
	};

	return (
		<>
			<BreadcrumbsItem to={match.path}>{translate(`add.new.service.${type}`)}</BreadcrumbsItem>
			<ConditionalRender
				show={!loading}
				onTrue={() => (
					<Switch>
						<Route
							path={`/dashboard/services/add/${accountId}/business-data`}
							render={() => (
								<MobileService
									users={filteredUsers}
									billingAccounts={billingAccounts}
									back={back}
									account={selectedAccount}
									billingAccountTypes={['INTERNET']}
									availableServices={[serviceType.CREDIT_LIMIT, serviceType.MOBILE_INTERNET_PLANS]}
									showConfirmationDialog={showConfirmationDialog}
								/>
							)}
						/>
						<Route
							path={`/dashboard/services/add/${accountId}/nama-plans`}
							render={() => (
								<MobileService
									users={filteredUsers}
									billingAccounts={billingAccounts}
									back={back}
									account={selectedAccount}
									billingAccountTypes={['MOBILE_POSTPAID']}
									availableServices={[
										serviceType.CREDIT_LIMIT,
										serviceType.MOBILE_INTERNET_PLANS,
										serviceType.GSM,
										serviceType.CPN,
									]}
									showConfirmationDialog={showConfirmationDialog}
								/>
							)}
						/>
						<Route
							path={`/dashboard/services/add/${accountId}/fiber-pro`}
							render={() => (
								<FixedBusinessBroadband
									back={back}
									account={selectedAccount}
									mainContainerRef={mainContainerRef}
								/>
							)}
						/>
						<Route
							path={`/dashboard/services/add/${accountId}/fixed`}
							render={() => (
								<PSTN back={back} account={selectedAccount} mainContainerRef={mainContainerRef} />
							)}
						/>
					{/* New route for adding new microbundle */}
						<Route
							path={`${ROUTES.ADD_SERVICES}${accountId}${ROUTES.ADD_NEW_MB}`}
							render={() => (
								<AddNewMb account={selectedAccount}/>
							)}
						/>
					</Switch>
				)}
			/>
		</>
	);
};

export default AddSingleService;
